import { WorkflowStatus } from './constants'
import {
  mapQueryNotificationsList,
  queryNotificationsList,
  queryNotificationsListCompact
} from './queries'
import graphqlService from '../../services/graphqlService'
import { DownloadReportTypes, PAGE, PAGE_SIZE } from '../../constants'
import { FilterParams, SORT_ORDER } from '../../interfaces'
import { getSortDirection, SortParams } from '../../utils/sortUtil'
import { RootState } from '../../rootReducer'
import { getGlobalParams } from '../../utils/urlUtil'
import apiService from '../../services/api/apiService'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export enum NotificationStatus {
  pending = 'Pending'
}

export enum NotificationAttachmentStatus {
  pending = 'PENDING',
  available = 'AVAILABLE',
  expired = 'EXPIRED'
}

export type NotificationIconName = 'csv' | string
export type NotificationAttachment = {
  id: string
  name: string
  url: string
  notificationId: string
}

export type Notification = {
  id: string
  title: string
  updateTimestamp: string
  createdAt: string
  notifyEmails: string[]
  icon?: NotificationIconName
  attachments?: NotificationAttachment[]
  workflowStatus: WorkflowStatus
  module: {
    id: string
    type: string
    severity?: string
    assignee: string
    datasourceType: string
    datasourceId: string
  }
}

interface NotificationsState {
  list?: Notification[]
  listCompact?: Notification[]
  total?: number
  sort: SortParams
}

export type IGetListParams = {
  [PAGE]?: number
  [PAGE_SIZE]?: number
  filters?: FilterParams
  startDate?: number
}

export const initialState: NotificationsState = {
  sort: {
    column: 'updateTimestamp',
    direction: SORT_ORDER.DESC
  }
}

export const ACTION_NOTIFICATIONS_LIST_FETCH = 'notifications/list'
export const fetchNotificationsList = createAsyncThunk(
  ACTION_NOTIFICATIONS_LIST_FETCH,
  async (params: IGetListParams, { getState }) => {
    const queryParams = { ...params, ...getGlobalParams(getState() as RootState) }
    const resultRaw = await graphqlService.execute(queryNotificationsList(queryParams))
    return mapQueryNotificationsList(resultRaw)
  }
)

export const ACTION_NOTIFICATIONS_LIST_COMPACT_FETCH = 'notifications/list/compact'
export const fetchNotificationsListCompact = createAsyncThunk(
  ACTION_NOTIFICATIONS_LIST_COMPACT_FETCH,
  async (params: IGetListParams) => {
    const resultRaw = await graphqlService.execute(queryNotificationsListCompact(params))
    return mapQueryNotificationsList(resultRaw)
  }
)

export const ACTION_NOTIFICATIONS_CANCEL_WORKFLOW = 'notifications/cancel_workflow'
export const cancelNotificationWorkflow = createAsyncThunk(
  ACTION_NOTIFICATIONS_CANCEL_WORKFLOW,
  async (notificationId: string) => {
    const resultRaw = await apiService.cancelReportWorkflow(notificationId)
    return resultRaw
  }
)

export const ACTION_NOTIFICATIONS_DISABLE_WORKFLOW = 'notifications/disable_workflow'
export const getDisabledStatusForNotificationWorkflow = createAsyncThunk(
  ACTION_NOTIFICATIONS_DISABLE_WORKFLOW,
  async (reportType: DownloadReportTypes) => {
    const resultRaw = await apiService.getDisabledWorkflowStatusByType(reportType)
    return resultRaw
  }
)

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearList: (state) => {
      state.list = initialState.list
      state.total = initialState.total
    },
    clearListCompact: (state) => {
      state.listCompact = initialState.list
    },
    setSort: (state, { payload }) => {
      state.sort = getSortDirection(state.sort, payload.column)
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNotificationsList.fulfilled, (state, { payload }) => {
      state.list = payload.list
      state.total = payload.total
    })
    builder.addCase(fetchNotificationsListCompact.fulfilled, (state, { payload }) => {
      state.listCompact = payload.list
    })
  }
})

export const { clearListCompact, clearList, setSort } = notificationsSlice.actions

export default notificationsSlice.reducer
